import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './EventVotePage.module.css';
import { eventVoteApi } from '../../../services/eventVoteApi';

interface User {
  User_ID: string;
  firstname?: string;
  lastname?: string;
  title?: string;
  mail?: string;
  jobtitle?: string;
  picture?: string;
  profile?: string;
  linkedin?: string;
  city?: {
    name: string;
  };
  country?: {
    abbrev: string;
  };
  firm?: {
    name: string;
    abbrev: string;
    logo?: string;
  };
}

interface Event {
  Events_ID: string;
  title: string;
  sub_title?: string;
  event_date?: string;
  vote_end_date?: string;
  is_future: boolean;
  voting_open: boolean;
}

interface Vote {
  Vote_ID: string;
  Selected_User_ID: string;
  Rank: number;
}

interface EventVoteData {
  event: Event;
  users: User[];
  best_matches: any[];
  selections: any[];
  votes: Vote[];
  user_already_selected: boolean;
  user_already_voted: boolean;
  matches_generated: boolean;
}

interface UserRank {
  user: User;
  rank: number;
}

const EventVotePage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<EventVoteData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [userRanks, setUserRanks] = useState<UserRank[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const sortableRef = useRef<HTMLDivElement>(null);
  const sortableInstance = useRef<any>(null);

  useEffect(() => {
    if (eventId) {
      fetchEventVoteData();
    }
  }, [eventId]);

  useEffect(() => {
    if (data && data.users.length > 0) {
      initializeUserRanks();
    }
  }, [data]);

  useEffect(() => {
    // Cleanup on component unmount
    return () => {
      destroySortable();
    };
  }, []);

  useEffect(() => {
    if (userRanks.length > 0 && !data?.matches_generated && sortableRef.current) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        initializeSortable();
      }, 50);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [userRanks.length, data?.matches_generated]);

  const destroySortable = () => {
    if (sortableInstance.current) {
      try {
        sortableInstance.current.destroy();
      } catch (error) {
        console.warn('Error destroying sortable instance:', error);
      } finally {
        sortableInstance.current = null;
      }
    }
  };

  const fetchEventVoteData = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await eventVoteApi.getEventVote(eventId!);
      setData(response);
    } catch (error: any) {
      console.error('Error fetching event vote data:', error);
      
      let displayErrorMessage = 'Failed to load event data. Please try again.';
      
      if (error.message.includes('404')) {
        displayErrorMessage = 'Event not found. The event may have been deleted or the URL is incorrect.';
        setErrorMessage(displayErrorMessage);
        toast.error(displayErrorMessage, {
          position: "top-right",
          autoClose: 5000,
          onClose: () => navigate('/events')
        });
      } else if (error.message.includes('403')) {
        displayErrorMessage = 'You are not registered for this event';
        setErrorMessage(displayErrorMessage);
        toast.error(displayErrorMessage, {
          position: "top-right",
          autoClose: 5000,
          onClose: () => navigate('/events')
        });
      } else if (error.message.includes('401')) {
        displayErrorMessage = 'Please log in to access this page';
        setErrorMessage(displayErrorMessage);
        toast.error(displayErrorMessage, {
          position: "top-right",
          autoClose: 5000,
          onClose: () => navigate('/login')
        });
      } else if (error.message.includes('You have not yet selected matches')) {
        displayErrorMessage = 'You have not yet selected matches. Please select matches first.';
        setErrorMessage(displayErrorMessage);
        toast.error(displayErrorMessage, {
          position: "top-right",
          autoClose: 5000,
          onClose: () => navigate(`/events/${eventId}/participants/`)
        });
      } else {
        setErrorMessage(displayErrorMessage);
        toast.error(displayErrorMessage, {
          position: "top-right",
          autoClose: 5000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const initializeUserRanks = () => {
    if (!data) return;

    let initialRanks: UserRank[] = [];

    if (data.user_already_voted && data.votes.length > 0) {
      // User hat bereits gevoted - verwende vorhandene Votes
      const sortedVotes = [...data.votes].sort((a, b) => a.Rank - b.Rank);
      initialRanks = sortedVotes.map(vote => {
        const user = data.users.find(u => u.User_ID === vote.Selected_User_ID);
        return {
          user: user!,
          rank: vote.Rank
        };
      }).filter(item => item.user);
    } else {
      // User hat noch nicht gevoted - verwende Selections in ursprünglicher Reihenfolge
      initialRanks = data.users.map((user, index) => ({
        user,
        rank: index + 1
      }));
    }

    setUserRanks(initialRanks);
  };

  const initializeSortable = async () => {
    if (!sortableRef.current) {
      console.warn('Sortable ref not available');
      return;
    }
    
    // Destroy existing instance if any
    destroySortable();

    try {
      // Dynamisch Sortable.js importieren
      const Sortable = (await import('sortablejs')).default;
      
      // Ensure the element is still in the DOM
      if (!sortableRef.current || !document.contains(sortableRef.current)) {
        console.warn('Sortable element not in DOM');
        return;
      }
      
      sortableInstance.current = Sortable.create(sortableRef.current, {
        animation: 300,
        easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        ghostClass: styles.sortableGhost,
        chosenClass: styles.sortableChosen,
        dragClass: styles.sortableDrag,
        // handle: `.${styles.dragIcon}`, // Entfernt - jetzt kann man überall anfassen
        disabled: false,
        fallbackOnBody: true,
        swapThreshold: 0.65,
        invertSwap: false,
        direction: 'vertical',
        touchStartThreshold: 3,
        delay: 100,
        preventOnFilter: false,
        onStart: (evt: any) => {
          console.log('Drag started');
          // Add visual feedback
          if (evt.item) {
            evt.item.style.transform = 'scale(1.02)';
            evt.item.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
          }
        },
        onEnd: (evt: any) => {
          console.log('Drag ended', evt.oldIndex, evt.newIndex);
          // Reset visual feedback
          if (evt.item) {
            evt.item.style.transform = '';
            evt.item.style.boxShadow = '';
          }
          if (evt.oldIndex !== undefined && evt.newIndex !== undefined && evt.oldIndex !== evt.newIndex) {
            handleRankChange(evt.oldIndex, evt.newIndex);
          }
        },
        onMove: (evt: any) => {
          // Smooth movement feedback
          return true;
        }
      });
      
      console.log('Sortable initialized successfully');
    } catch (error) {
      console.error('Failed to load Sortable.js:', error);
      toast.error('Failed to initialize drag and drop functionality', {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleRankChange = (oldIndex: number, newIndex: number) => {
    if (oldIndex === newIndex) return;
    
    const newUserRanks = [...userRanks];
    const movedItem = newUserRanks.splice(oldIndex, 1)[0];
    newUserRanks.splice(newIndex, 0, movedItem);
    
    // Ranks neu zuweisen
    const updatedRanks = newUserRanks.map((item, index) => ({
      ...item,
      rank: index + 1
    }));
    
    setUserRanks(updatedRanks);
    
    // Ensure sortable remains functional after state change
    if (sortableInstance.current) {
      // Force a small delay to let React update the DOM
      setTimeout(() => {
        if (sortableInstance.current && sortableRef.current) {
          // Re-enable if needed
          sortableInstance.current.option("disabled", false);
        }
      }, 10);
    }
  };

  const handleSaveVotes = async () => {
    if (!data || !eventId) return;

    try {
      setSaving(true);
      
      const votes = userRanks.map(item => ({
        selected_user_id: item.user.User_ID,
        rank: item.rank
      }));
      
      await eventVoteApi.saveEventVotes(eventId, votes);
      
      toast.success('Votes saved successfully!', {
        position: "top-right",
        autoClose: 3000
      });

      // Update data to reflect that user has voted
      if (data) {
        const updatedData = {
          ...data,
          user_already_voted: true,
          votes: votes.map((vote) => ({
            Vote_ID: `temp_${vote.selected_user_id}`,
            Selected_User_ID: vote.selected_user_id,
            Rank: vote.rank
          }))
        };
        setData(updatedData);
        
        // Reinitialize sortable after state update
        setTimeout(() => {
          if (!updatedData.matches_generated) {
            initializeSortable();
          }
        }, 100);
      }
    } catch (error: any) {
      console.error('Error saving votes:', error);
      toast.error('Failed to save votes. Please try again.', {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  const getDefaultProfileImage = () => {
    return '/static/images/user-profile-icon.png';
  };

  const getDefaultCompanyLogo = () => {
    return '/static/images/No_image_available.png';
  };

  if (loading) {
    return (
      <div className="Root">
        <div className="container mt-4">
          <div className={styles.loadingSpinner}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="Root">
        <div className="container mt-4">
          <div className={`alert alert-danger ${styles.errorAlert}`} role="alert">
            {errorMessage || 'Failed to load event data. Please try again.'}
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  if (data.matches_generated) {
    return (
      <div className="Root">
        <div className="container mt-4">
          {/* Breadcrumb Navigation */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/events/" className="text-decoration-none">Events</a>
              </li>
              <li className="breadcrumb-item">
                <span>{data.event.title}</span>
              </li>
              <li className="breadcrumb-item">
                <a href={`/events/${data.event.Events_ID}/participants`} className="text-decoration-none">
                  Choose your Matches
                </a>
              </li>
              <li className="breadcrumb-item active">
                <span>Your Match Ranking</span>
              </li>
            </ol>
          </nav>

          {/* Main Content Card */}
          <div className="card p-4" style={{ width: '100%' }}>
            <div className={styles.roleHeader}>
              <i className="bi bi-calendar3-event"></i>
              <h4 className="mb-0">
                Event - {data.event.title} - <strong>Your Match Ranking</strong>
              </h4>
            </div>
            <hr />

            <div className={styles.voting}>
              <h1>✔️</h1>
              <p>
                The matches have already been generated, so ranking is no longer possible.
              </p>
              <a href={`/events/${data.event.Events_ID}/rounds/`} className="btn btn-primary">
                Watch the matches
              </a>
            </div>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="Root">
      <div className="container mt-4">
        {/* Breadcrumb Navigation */}
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/events/" className="text-decoration-none">Events</a>
            </li>
            <li className="breadcrumb-item">
              <span>{data.event.title}</span>
            </li>
            <li className="breadcrumb-item">
              <a href={`/events/${data.event.Events_ID}/participants`} className="text-decoration-none">
                Choose your Matches
              </a>
            </li>
            <li className="breadcrumb-item active">
              <span>Your Match Ranking</span>
            </li>
          </ol>
        </nav>

        {/* Main Content Card */}
        <div className="card p-4" style={{ width: '100%' }}>
          <div className={styles.roleHeader}>
            <i className="bi bi-calendar3-event"></i>
            <h4 className="mb-0">
              Event - {data.event.title} - <strong>Your Match Ranking</strong>
            </h4>
            {data.event.sub_title && (
              <p>
                <i 
                  className="bi bi-info text-primary" 
                  data-bs-toggle="popover" 
                  data-bs-placement="top"
                  data-bs-content={data.event.sub_title}
                  data-bs-html="true" 
                  tabIndex={0}
                ></i>
              </p>
            )}
          </div>
          <hr />

          {/* Status Message */}
          <div className="row">
            <div className={`${styles.selectionStatus} ${data.user_already_voted ? styles.success : styles.error}`}>
              <p>
                {data.user_already_voted 
                  ? "You have already voted but you can still change your vote, then click on 'save'."
                  : "You have not yet voted. Please vote as you wish and then click on 'save'."
                }
              </p>
            </div>
          </div>

          {/* Instruction Text */}
          <div className={styles.instructionText}>
            <i className="bi bi-info-circle me-2"></i>
            <strong>How to rank:</strong> Click and drag anywhere on a person's card to move them up or down. 
            Drag the person you want to meet most to the top. Your preferences help create the best possible matches for you.
            <br />
            <small className="text-muted mt-1 d-block">
              💡 <strong>Tip:</strong> The 
              <i className="bi bi-grip-vertical mx-1" style={{fontSize: '1.1em', color: '#007bff'}}></i>
              icon shows this item can be dragged. On mobile, tap and hold anywhere on the card to start dragging.
            </small>
          </div>

          <hr />

          {/* Sortable List */}
          <div className="row">
            <div className="col-md-12">
              <div className={styles.listContainer}>
                {/* List Header */}
                <div className={styles.listHeader}>
                  <span>Photo</span>
                  <span>Name & Position</span>
                  <span>Company</span>
                  <span>Drag</span>
                </div>

                {/* Sortable List Items */}
                <div 
                  ref={sortableRef} 
                  className={styles.sortableList}
                  key={`sortable-${userRanks.length}-${data?.user_already_voted ? 'voted' : 'not-voted'}`}
                >
                  {userRanks.map((item, index) => (
                    <div
                      key={`user-${item.user.User_ID}-${item.rank}`}
                      className={styles.listItem}
                      data-user-id={item.user.User_ID}
                    >
                      {/* Rank Indicator */}
                      <div className={styles.rankIndicator}>
                        {item.rank}
                      </div>

                      {/* Profile Image */}
                      <div className="me-2">
                        <img 
                          src={item.user.picture ? `data:image/png;base64,${item.user.picture}` : getDefaultProfileImage()}
                          className={styles.profileImg}
                          alt="User Profile"
                          draggable={false}
                        />
                      </div>

                      {/* User Details */}
                      <div className={styles.userDetails}>
                        <span className={styles.userName}>
                          {[item.user.title, item.user.firstname, item.user.lastname].filter(Boolean).join(' ')}
                        </span>
                        <span className={styles.userPosition}>
                          {item.user.jobtitle || ''}
                        </span>
                      </div>

                      {/* Company Info */}
                      <div className={styles.companyInfo}>
                        <img 
                          src={item.user.firm?.logo ? `data:image/png;base64,${item.user.firm.logo}` : getDefaultCompanyLogo()}
                          className={styles.companyLogo}
                          alt={item.user.firm?.abbrev || 'Company Logo'}
                          draggable={false}
                        />
                        <span className={styles.companyName}>
                          {item.user.firm?.name || ''}
                        </span>
                      </div>

                      {/* Drag Icon */}
                      <div className={styles.dragIcon}>
                        <i className="bi bi-grip-vertical"></i>
                        <span className="visually-hidden">Drag to reorder</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            className={`${styles.saveButton} btn btn-primary`}
            onClick={handleSaveVotes}
            disabled={saving}
          >
            <i className="bi bi-check-lg me-1"></i>
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default EventVotePage;
